import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  AttachmentOwnerType,
  ServiceOrderCategory,
  ServiceOrderStatus,
  ServiceSeverity,
  UserRole,
  type CustomerSummaryDto,
  type StoreSummaryDto,
} from '@bhops/shared';
import {
  adminServiceOrdersApi,
  serviceOrdersApi,
  type ListServiceOrdersQuery,
} from '@/api/service-orders';
import { attachmentsApi } from '@/api/attachments';
import { extractApiError } from '@/api/client';
import { kstDateTime } from '@/lib/format-date';
import { auditEventsApi } from '@/api/audit-events';
import { Button, Field, Textarea } from '@/components/Form';
import { ConfirmActionModal } from '@/components/ConfirmActionModal';
import { CustomerPicker } from '@/components/CustomerPicker';
import { StorePicker } from '@/components/StorePicker';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
import { useUserLookup } from '@/hooks/useUserLookup';
import { allowedNextStatuses, isTerminalStatus } from '@/lib/service-order-state-machine';
import { formatServiceOrderEvent } from '@/lib/service-order-event-format';
import { StatusBar } from '@/components/StatusBar';
import { Icon } from '@/components/Icon';
import { ReportsInboxPage } from './ReportsInboxPage';
import { ServiceOrdersTriagePage } from './ServiceOrdersTriagePage';

// plan-field-service-orders.md §F-6 — Service Order inbox.
//
// Mirrors ReportsInboxPage's two-pane layout: left = filtered
// table, right = detail rail with state-machine action buttons.
// Filters: status / severity / category / search (humanId or
// description ILIKE). Transitions and cancel write through the
// shared API client; mutations invalidate both list + detail
// queries.

const STATUS_LABEL: Record<ServiceOrderStatus, string> = {
  OPEN: '신규',
  TRIAGED: '분류완료',
  ASSIGNED: '배정',
  IN_PROGRESS: '진행중',
  BLOCKED: '대기',
  RESOLVED: '해결',
  VERIFIED: '검증',
  CLOSED: '종결',
  CANCELLED: '취소',
};

// round7 §1-5(c) — 등급 한글 병기 + SLA 의미 툴팁(트리아지/해결/검증 기한).
const SEVERITY_LABEL: Record<ServiceSeverity, string> = {
  P1: 'P1 · 긴급',
  P2: 'P2 · 일반',
  P3: 'P3 · 낮음',
};
const SEVERITY_TOOLTIP: Record<ServiceSeverity, string> = {
  P1: '영업을 못 할 정도의 긴급 건입니다. 분류 30분, 해결 4시간, 검증 24시간 안에 처리합니다.',
  P2: '기능에 문제가 있는 일반 건입니다. 분류 4시간, 해결 24시간, 검증 7일 안에 처리합니다.',
  P3: '가벼운 문제나 문의입니다. 분류 24시간, 해결 7일, 검증 30일 안에 처리합니다.',
};

/** 상태 이름 뒤에 '로/으로'를 받침에 맞게 붙인다(ㄹ 받침은 '로'). */
function withRo(word: string): string {
  const code = word.charCodeAt(word.length - 1);
  if (code < 0xac00 || code > 0xd7a3) return `${word}(으)로`;
  const jong = (code - 0xac00) % 28;
  return `${word}${jong === 0 || jong === 8 ? '로' : '으로'}`;
}

const CATEGORY_LABEL: Record<ServiceOrderCategory, string> = {
  PREVENTIVE: '정기점검',
  REACTIVE: '긴급조치',
  INSTALL: '설치',
  REMOVAL: '회수',
  PARTS_SWAP: '부품교체',
};

const PAGE_SIZE = 50;

// design_handoff_kuun_ux/README.md §⑥ — service order status → report stage (좌측 컬러바용).
// CustomerReport 6단계와 매핑 (디자인의 report color palette 재사용).
function statusToReportStage(
  s: ServiceOrderStatus,
): 'new' | 'triaged' | 'assigned' | 'inProgress' | 'resolved' | 'dismissed' {
  switch (s) {
    case ServiceOrderStatus.OPEN:
      return 'new';
    case ServiceOrderStatus.TRIAGED:
      return 'triaged';
    case ServiceOrderStatus.ASSIGNED:
      return 'assigned';
    case ServiceOrderStatus.IN_PROGRESS:
    case ServiceOrderStatus.BLOCKED:
      return 'inProgress';
    case ServiceOrderStatus.RESOLVED:
    case ServiceOrderStatus.VERIFIED:
    case ServiceOrderStatus.CLOSED:
      return 'resolved';
    case ServiceOrderStatus.CANCELLED:
      return 'dismissed';
  }
}

// 추가수정 2차(26-07-27) §2 — 브라우저 로캘(PC 시간대) 대신 KST 고정.
// SLA 마감·타임라인은 시각이 핵심이라 날짜만으로 줄이지 않는다.
function fmtKrTime(iso: string | null | undefined): string {
  return kstDateTime(iso) ?? '–';
}

// 서버 addNote 는 NOTE_ADDED payload 를 `{ body }` 로 적재하는데 공용
// 포매터(service-order-event-format)는 `payload.note` 만 읽어 본문이 빈
// 칸으로 떨어진다. 타임라인에서 메모 본문이 사라지지 않도록 여기서 직접
// 읽는다.
function noteBodyOf(evt: { type: string; payload: unknown }): string | null {
  if (evt.type !== 'NOTE_ADDED') return null;
  const body = (evt.payload as { body?: unknown } | null | undefined)?.body;
  return typeof body === 'string' && body.length > 0 ? body : null;
}

// round7 D5 — 신청자 휴대폰은 원문 표시(연락 업무용 — 마스킹 제거).
// S3 Phase 3.1 — 분류 큐를 별도 사이드바 항목/라우트에서 분리하지 않고
// 서비스 오더 페이지의 내부 탭으로 통합. `?view` 쿼리 파라미터로 딥링크
// 가능 (undefined/inbox = 전체 인박스, triage = 분류 큐). 탭 마크업/스타일은
// RobotDetailPage 의 role="tablist" + TabButton 패턴을 그대로 따른다.
// round5 #14 — 'SO 인박스 단일화': 고객 리포트(접수 원본) 탭 추가. 모든
// 리포트가 REACTIVE SO 로 자동 승격돼 같은 인시던트가 두 인박스에 이중으로
// 뜨던 문제 — 운영자가 보는 화면을 이곳 하나로 모은다(/reports 는 redirect).
type InboxTab = 'inbox' | 'triage' | 'reports';

const INBOX_TABS: readonly InboxTab[] = ['inbox', 'triage', 'reports'];

export function ServiceOrdersInboxPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawView = searchParams.get('view') ?? 'inbox';
  const view: InboxTab = INBOX_TABS.includes(rawView as InboxTab) ? (rawView as InboxTab) : 'inbox';

  // 잘못된 `?view=...` (오타/오래된 딥링크) 은 제거해 탭이 키보드로 다시
  // 도달 가능하도록 하고 URL 이 실제 렌더된 탭을 반영하게 함.
  useEffect(() => {
    if (rawView !== view) {
      const params = new URLSearchParams(searchParams);
      params.delete('view');
      setSearchParams(params, { replace: true });
    }
  }, [rawView, view]);

  function setView(next: InboxTab): void {
    const params = new URLSearchParams(searchParams);
    // 기본 탭(inbox) 은 깔끔한 URL 을 위해 파라미터 자체를 제거.
    if (next === 'inbox') params.delete('view');
    else params.set('view', next);
    // replace 로 back-button 스팸 방지 (RobotDetailPage setTab 과 동일).
    setSearchParams(params, { replace: true });
  }

  function handleTabKeyDown(e: KeyboardEvent<HTMLButtonElement>, currentTab: InboxTab): void {
    const idx = INBOX_TABS.indexOf(currentTab);
    if (idx < 0) return;
    let next: InboxTab | null = null;
    if (e.key === 'ArrowRight') next = INBOX_TABS[(idx + 1) % INBOX_TABS.length];
    else if (e.key === 'ArrowLeft')
      next = INBOX_TABS[(idx - 1 + INBOX_TABS.length) % INBOX_TABS.length];
    else if (e.key === 'Home') next = INBOX_TABS[0];
    else if (e.key === 'End') next = INBOX_TABS[INBOX_TABS.length - 1];
    if (next) {
      e.preventDefault();
      const target = next;
      setView(target);
      requestAnimationFrame(() => {
        document.getElementById(`so-tab-${target}`)?.focus();
      });
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <div
        role="tablist"
        aria-label="서비스 오더 보기"
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid var(--border-subtle)',
          flexWrap: 'wrap',
        }}
      >
        <InboxTabButton
          tabKey="inbox"
          active={view === 'inbox'}
          onClick={() => setView('inbox')}
          onKeyDown={(e) => handleTabKeyDown(e, 'inbox')}
        >
          전체 인박스
        </InboxTabButton>
        <InboxTabButton
          tabKey="triage"
          active={view === 'triage'}
          onClick={() => setView('triage')}
          onKeyDown={(e) => handleTabKeyDown(e, 'triage')}
        >
          분류 큐
        </InboxTabButton>
        <InboxTabButton
          tabKey="reports"
          active={view === 'reports'}
          onClick={() => setView('reports')}
          onKeyDown={(e) => handleTabKeyDown(e, 'reports')}
        >
          고객 리포트 (접수 원본)
        </InboxTabButton>
      </div>

      {/* 탭 패널. 각 뷰는 자체 hook (쿼리/뮤테이션) 을 갖는 컴포넌트로
          분리해 비활성 탭의 데이터 패칭이 일어나지 않게 함. 분류 큐는
          self-contained 페이지(ServiceOrdersTriagePage) 를 그대로 재사용. */}
      {view === 'inbox' && (
        <div
          role="tabpanel"
          id="so-panel-inbox"
          aria-labelledby="so-tab-inbox"
          style={{ flex: 1, minHeight: 0 }}
        >
          <InboxView />
        </div>
      )}
      {view === 'triage' && (
        <div
          role="tabpanel"
          id="so-panel-triage"
          aria-labelledby="so-tab-triage"
          style={{ flex: 1, minHeight: 0 }}
        >
          <ServiceOrdersTriagePage />
        </div>
      )}
      {view === 'reports' && (
        <div
          role="tabpanel"
          id="so-panel-reports"
          aria-labelledby="so-tab-reports"
          style={{ flex: 1, minHeight: 0 }}
        >
          {/* round5 #14 — self-contained 페이지 재사용(분류 큐와 동일 패턴). */}
          <ReportsInboxPage />
        </div>
      )}
    </div>
  );
}

function InboxTabButton({
  tabKey,
  active,
  onClick,
  onKeyDown,
  children,
}: {
  tabKey: InboxTab;
  active: boolean;
  onClick: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLButtonElement>) => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      id={`so-tab-${tabKey}`}
      aria-selected={active}
      aria-controls={`so-panel-${tabKey}`}
      tabIndex={active ? 0 : -1}
      onClick={onClick}
      onKeyDown={onKeyDown}
      style={{
        padding: '10px 14px',
        border: 'none',
        background: 'transparent',
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        color: active ? 'var(--fg-brand)' : 'var(--fg-muted)',
        borderBottom: `2px solid ${active ? 'var(--brand)' : 'transparent'}`,
        cursor: 'pointer',
        marginBottom: -1,
      }}
    >
      {children}
    </button>
  );
}

// 전체 인박스 뷰 — 기존 ServiceOrdersInboxPage 본문 전체. S3 Phase 3.1 에서
// 탭 wrapper 와 분리하기 위해 InboxView 로 이름만 변경 (로직 변화 없음).
function InboxView() {
  const toast = useToast();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { resolve: resolveUser } = useUserLookup();
  const canEdit =
    user?.role === UserRole.ADMIN ||
    user?.role === UserRole.MANAGER ||
    user?.role === UserRole.FIELD_MANAGER ||
    user?.role === UserRole.MFG_MANAGER ||
    user?.role === UserRole.SALES_MANAGER;

  const [filters, setFilters] = useState<ListServiceOrdersQuery>({
    page: 1,
    pageSize: PAGE_SIZE,
  });
  const [searchInput, setSearchInput] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 계획 §8-2 — 타임라인 메모 입력값. 다른 SO 로 넘어가면 초안이 따라가지
  // 않도록 선택이 바뀔 때 비운다.
  const [noteBody, setNoteBody] = useState('');
  useEffect(() => {
    setNoteBody('');
  }, [selectedId]);

  // docs/work_log/p1-stale-links — `<Link to="/service-orders" state={{ openId }}>`
  // 패턴 (PartLotImpactPage / RobotDetail FieldServiceTab / ReportsInbox 의 SO
  // 전환 핸들러) 이 보낸 deep-link 를 받아 자동 선택. router.location.state 는
  // navigate 시 1회만 유효하므로 useEffect 에서 즉시 흡수 → null 로 리셋해
  // 새로고침 시 반복 적용되지 않도록 함.
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    // R2 후속(워크플로 M5/M10) — state.openId(정상 딥링크)뿐 아니라 ?focus= 쿼리
    // (대시보드 '다가오는 일정' / 통합 캘린더의 SO 링크)도 받아 자동 선택한다.
    const focusId = searchParams.get('focus');
    const incomingId =
      (location.state as { openId?: string } | null)?.openId ?? focusId ?? undefined;
    if (incomingId && incomingId !== selectedId) {
      setSelectedId(incomingId);
      if (focusId) {
        const params = new URLSearchParams(searchParams);
        params.delete('focus');
        setSearchParams(params, { replace: true });
      } else {
        navigate(location.pathname, { replace: true, state: null });
      }
    }
    // selectedId 는 의존 제외(재트리거 방지). location.state + 쿼리 변화에만 반응.
  }, [location.state, searchParams]);
  // docs/work_log/v1026-remaining-work-batch-7 — 공개 채널 (PUB) 만 필터.
  // ServiceOrder.humanId 가 `SO-YYYYMMDD-PUB###` 패턴 인 row 만 통과.
  // 운영자가 공개 신청만 즉시 확인하려는 케이스 (DSAR / SLA / inbox triage).
  const [pubOnly, setPubOnly] = useState(false);
  // docs/work_log/v1031-remaining-work-batch-12 §6 — 공개 신청 list row 의
  // 마스킹된 reporter phone 표시 옵션. 기본 OFF (PII 노출 최소화).
  // ON 시 humanId 옆에 `010-****-5678` 형식으로 노출 → 운영자가 detail 열지 않고도
  // 즉시 식별 가능. 매장명은 자유 텍스트라 list 폭 문제로 detail 에서만 노출.
  const [showReporter, setShowReporter] = useState(false);
  // round7 §1-5 (D4) — ADMIN 전용 '삭제됨 포함' 토글. soft-delete 된 SO 를
  // 목록에 노출해 복구할 수 있게 한다(robots/customers 의 includeDeleted 와 동일).
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // Confirmation-modal state for the three flows that previously
  // chained window.prompt calls. Each modal is fully controlled:
  // `open` flips on click, the form values live alongside, and the
  // mutation's onSuccess closes + resets in one shot.
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [restoreReason, setRestoreReason] = useState('');
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  // round7 §1-5 (D5) — 등급(severity) 재분류 모달.
  const [severityModalOpen, setSeverityModalOpen] = useState(false);
  const [severityValue, setSeverityValue] = useState<ServiceSeverity>(ServiceSeverity.P2);
  const [severityReason, setSeverityReason] = useState('');
  // Pickers hold full DTOs (not bare ids) so we can render the
  // selected entity's label/sublabel without a follow-up fetch.
  // Mutation submit reads `.id` off these when dispatching.
  const [reassignCustomer, setReassignCustomer] = useState<CustomerSummaryDto | null>(null);
  const [reassignStore, setReassignStore] = useState<StoreSummaryDto | null>(null);
  const [reassignReason, setReassignReason] = useState('');

  // batch-8 — pubOnly 를 list query param `origin: 'PUBLIC'` 로 전송.
  // backend 에서 정확히 filter → pagination 정확도 (이전 client-side filter 는
  // 현재 page 내 filter 만 가능했음).
  const effectiveFilters = {
    ...filters,
    ...(pubOnly ? { origin: 'PUBLIC' as const } : {}),
    // round7 §1-5 — 서버가 ADMIN 일 때만 적용(비-ADMIN 은 무시).
    ...(includeDeleted ? { includeDeleted: true } : {}),
  };
  const listQ = useQuery({
    queryKey: ['service-orders', effectiveFilters],
    queryFn: () => serviceOrdersApi.list(effectiveFilters),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const selectedQ = useQuery({
    queryKey: ['service-order', selectedId],
    queryFn: () => serviceOrdersApi.get(selectedId!),
    enabled: Boolean(selectedId),
  });

  // round7 §1-6 (D7) — 공개 접수 사진(+운영자 첨부). attachments 테이블의
  // ownerType=ServiceOrder 행을 presigned GET URL 과 함께 가져온다.
  const attachmentsQ = useQuery({
    queryKey: ['service-order-attachments', selectedId],
    queryFn: () => attachmentsApi.list(AttachmentOwnerType.SERVICE_ORDER, selectedId!),
    enabled: Boolean(selectedId),
    staleTime: 30_000,
  });

  const transitionMx = useMutation({
    mutationFn: (args: { id: string; to: ServiceOrderStatus; note?: string }) =>
      serviceOrdersApi.transition(args.id, { to: args.to, note: args.note }),
    onSuccess: (_, args) => {
      qc.invalidateQueries({ queryKey: ['service-orders'] });
      qc.invalidateQueries({ queryKey: ['service-order', args.id] });
      toast.success(`${STATUS_LABEL[args.to]} 상태로 바꿨습니다.`);
    },
    onError: (err) => toast.error(extractApiError(err), '상태 변경 실패'),
  });

  const cancelMx = useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      serviceOrdersApi.cancel(args.id, { reason: args.reason }),
    onSuccess: (_, args) => {
      qc.invalidateQueries({ queryKey: ['service-orders'] });
      qc.invalidateQueries({ queryKey: ['service-order', args.id] });
      toast.success('서비스 오더를 취소했습니다.');
      setCancelModalOpen(false);
      setCancelReason('');
    },
    onError: (err) => toast.error(extractApiError(err), '취소 실패'),
  });

  // 계획 §8-2 — 타임라인 메모. 서버가 NOTE_ADDED 이벤트로 적재하므로 상세
  // 쿼리만 무효화하면 타임라인이 곧바로 갱신된다. 종결/취소된 SO 에도
  // 남길 수 있다(서버에서 terminal 차단을 뺐음).
  const addNoteMx = useMutation({
    mutationFn: (args: { id: string; body: string }) =>
      serviceOrdersApi.addNote(args.id, {
        // 서버는 이 id 를 이벤트 row id 겸 멱등키로 쓴다.
        id: globalThis.crypto?.randomUUID?.() ?? `note-${Date.now()}-${Math.random()}`,
        body: args.body,
      }),
    onSuccess: (_, args) => {
      qc.invalidateQueries({ queryKey: ['service-order', args.id] });
      setNoteBody('');
      toast.success('메모를 추가했습니다.');
    },
    onError: (err) => toast.error(extractApiError(err), '메모 추가 실패'),
  });

  // v0.5.x — admin restore. Surfaces only when the row is in a
  // terminal status AND the caller is ADMIN. Backend hard-gates
  // both with @Roles + a 400 on non-terminal source.
  const isAdmin = user?.role === UserRole.ADMIN;
  const restoreMx = useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      adminServiceOrdersApi.restore(args.id, { reason: args.reason }),
    onSuccess: (_, args) => {
      qc.invalidateQueries({ queryKey: ['service-orders'] });
      qc.invalidateQueries({ queryKey: ['service-order', args.id] });
      toast.success('서비스 오더를 복구했습니다.');
      setRestoreModalOpen(false);
      setRestoreReason('');
    },
    onError: (err) => toast.error(extractApiError(err), '복구 실패'),
  });

  // v0.5.x — admin reassign-customer. Surfaces only when the row
  // is in a NON-terminal status (terminal rows reject with 400
  // and the message points back at /restore first) AND the caller
  // is ADMIN. Server-side hard-gates both via @Roles + status
  // check. We mirror both gates at the UI layer so the button
  // doesn't show up on rows where it would 4xx.
  const reassignCustomerMx = useMutation({
    mutationFn: (args: {
      id: string;
      customerId: string;
      storeId: string | null;
      reason: string;
    }) =>
      adminServiceOrdersApi.reassignCustomer(args.id, {
        customerId: args.customerId,
        storeId: args.storeId,
        reason: args.reason,
      }),
    onSuccess: (_, args) => {
      qc.invalidateQueries({ queryKey: ['service-orders'] });
      qc.invalidateQueries({ queryKey: ['service-order', args.id] });
      toast.success('고객을 재배정했습니다.');
      setReassignModalOpen(false);
      setReassignCustomer(null);
      setReassignStore(null);
      setReassignReason('');
    },
    onError: (err) => toast.error(extractApiError(err), '고객 재배정 실패'),
  });

  // round7 §1-5 (D4) — soft-delete (매니저 이상). 잘못 생성/중복 SO 를
  // 목록에서 제거. ADMIN 은 '삭제됨 포함' 토글 + 복구로 되살릴 수 있다.
  const deleteMx = useMutation({
    mutationFn: (id: string) => serviceOrdersApi.remove(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['service-orders'] });
      qc.invalidateQueries({ queryKey: ['service-order', id] });
      toast.success('서비스 오더를 삭제했습니다.');
    },
    onError: (err) => toast.error(extractApiError(err), '삭제 실패'),
  });

  // round7 §1-5 (D4) — soft-delete 복구(ADMIN). deletedAt 만 해제.
  const restoreDeletedMx = useMutation({
    mutationFn: (id: string) => adminServiceOrdersApi.restoreDeleted(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['service-orders'] });
      qc.invalidateQueries({ queryKey: ['service-order', id] });
      toast.success('삭제된 서비스 오더를 복구했습니다.');
    },
    onError: (err) => toast.error(extractApiError(err), '복구 실패'),
  });

  // round7 §1-5 (D5) — 등급 재분류(SLA 마감 재계산 + 사유 감사).
  const severityMx = useMutation({
    mutationFn: (args: { id: string; severity: ServiceSeverity; reason: string }) =>
      serviceOrdersApi.changeSeverity(args.id, { severity: args.severity, reason: args.reason }),
    onSuccess: (_, args) => {
      qc.invalidateQueries({ queryKey: ['service-orders'] });
      qc.invalidateQueries({ queryKey: ['service-order', args.id] });
      toast.success(`${SEVERITY_LABEL[args.severity]} 등급으로 바꿨습니다.`);
      setSeverityModalOpen(false);
      setSeverityReason('');
    },
    onError: (err) => toast.error(extractApiError(err), '등급 변경 실패'),
  });

  // batch-8 — `ServiceOrder.origin` 컬럼 정식 추가 → backend query param 으로
  // 이전. client-side `/-PUB\d+$/` 정규식 매칭 제거. pubOnly toggle 은 listQ
  // 의 query param `origin=PUBLIC` 로 전송.
  const items = listQ.data?.items ?? [];

  // §⑥ Phase 2 — SLA 카운트다운 live re-render. 매분 ticks 변경 → 자식 row 의
  // formatSlaCountdown(...) 재계산. 페이지 mount/unmount 와 함께 자동 정리.
  const [, setTicks] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTicks((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // §⑥ Phase 2 — Severity summary (P1/P2/P3 카운트). 현재 page 의 items 기반.
  // (전 페이지 누적이 필요하면 별 endpoint 필요 — 추후 robotsApi.lifecycleCounts() 와 같은 패턴.)
  const severityCounts = useMemo(() => {
    const counts = { P1: 0, P2: 0, P3: 0 } as Record<ServiceSeverity, number>;
    for (const it of items) {
      counts[it.severity] = (counts[it.severity] ?? 0) + 1;
    }
    return counts;
  }, [items]);
  const detail = selectedQ.data;
  const nextStatuses = useMemo(
    () =>
      detail
        ? allowedNextStatuses(detail.status).filter((s) => s !== ServiceOrderStatus.CANCELLED)
        : [],
    [detail],
  );

  function applySearch(): void {
    setFilters((f) => ({
      ...f,
      search: searchInput.trim() || undefined,
      page: 1,
    }));
  }

  // The three handle* functions just open the matching modal. The
  // form state is owned at the page level (declared above) so the
  // modal can be a pure controlled component; submit handlers
  // below pull the trimmed values and dispatch the mutation.
  function handleCancel(): void {
    if (!detail) return;
    setCancelReason('');
    setCancelModalOpen(true);
  }

  function handleRestore(): void {
    if (!detail) return;
    setRestoreReason('');
    setRestoreModalOpen(true);
  }

  // round7 §1-5 (D4) — soft-delete. 사유 입력 없는 단순 제거이므로
  // window.confirm 으로 충분(취소/복구처럼 감사 사유가 필요한 흐름과 구분).
  function handleDelete(): void {
    if (!detail) return;
    if (
      window.confirm(
        `${detail.humanId} 서비스 오더를 삭제할까요?\n\n목록에서 사라집니다. 관리자는 '삭제됨 포함'에서 복구할 수 있습니다.`,
      )
    ) {
      deleteMx.mutate(detail.id);
    }
  }

  // round7 §1-5 (D5) — 등급 변경 모달 오픈. 현재 등급을 기본값으로.
  function handleChangeSeverity(): void {
    if (!detail) return;
    setSeverityValue(detail.severity);
    setSeverityReason('');
    setSeverityModalOpen(true);
  }

  function submitSeverity(): void {
    if (!detail) return;
    const reason = severityReason.trim();
    if (!reason || severityValue === detail.severity) return;
    severityMx.mutate({ id: detail.id, severity: severityValue, reason });
  }

  function handleReassignCustomer(): void {
    if (!detail) return;
    setReassignCustomer(null);
    setReassignStore(null);
    setReassignReason('');
    setReassignModalOpen(true);
  }

  // Submit handlers. Each pulls from the page-level form state,
  // trims, and dispatches the mutation. The corresponding modal's
  // submit button is wired to call this; close/reset on success
  // happens in the mutation's onSuccess.
  function submitCancel(): void {
    if (!detail) return;
    const reason = cancelReason.trim();
    if (!reason) return;
    cancelMx.mutate({ id: detail.id, reason });
  }

  function submitRestore(): void {
    if (!detail) return;
    const reason = restoreReason.trim();
    if (!reason) return;
    restoreMx.mutate({ id: detail.id, reason });
  }

  function submitReassignCustomer(): void {
    if (!detail) return;
    const reason = reassignReason.trim();
    if (!reassignCustomer || !reason) return;
    // Picker-null store → caller intentionally clears the store
    // pointer. Picker-selected → pass the id through; server
    // validates it belongs to the new customer in the same
    // ownerEntity.
    reassignCustomerMx.mutate({
      id: detail.id,
      customerId: reassignCustomer.id,
      storeId: reassignStore?.id ?? null,
      reason,
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      {/* §⑥ — Severity summary strip (P1 pulse). 카드 클릭 시 severity filter 토글. */}
      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}
        data-testid="severity-summary"
      >
        <SeverityCard
          severity={ServiceSeverity.P1}
          count={severityCounts.P1}
          active={filters.severity === ServiceSeverity.P1}
          onClick={() =>
            setFilters((f) => ({
              ...f,
              severity: f.severity === ServiceSeverity.P1 ? undefined : ServiceSeverity.P1,
              page: 1,
            }))
          }
        />
        <SeverityCard
          severity={ServiceSeverity.P2}
          count={severityCounts.P2}
          active={filters.severity === ServiceSeverity.P2}
          onClick={() =>
            setFilters((f) => ({
              ...f,
              severity: f.severity === ServiceSeverity.P2 ? undefined : ServiceSeverity.P2,
              page: 1,
            }))
          }
        />
        <SeverityCard
          severity={ServiceSeverity.P3}
          count={severityCounts.P3}
          active={filters.severity === ServiceSeverity.P3}
          onClick={() =>
            setFilters((f) => ({
              ...f,
              severity: f.severity === ServiceSeverity.P3 ? undefined : ServiceSeverity.P3,
              page: 1,
            }))
          }
        />
      </div>

      {/* Filter rail */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          서비스 오더
        </h1>
        <select
          value={filters.status ?? ''}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              status: (e.target.value || undefined) as ServiceOrderStatus | undefined,
              page: 1,
            }))
          }
          style={selectStyle}
        >
          <option value="">전체 상태</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <select
          value={filters.severity ?? ''}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              severity: (e.target.value || undefined) as ServiceSeverity | undefined,
              page: 1,
            }))
          }
          style={selectStyle}
        >
          <option value="">전체 등급</option>
          {Object.entries(SEVERITY_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <select
          value={filters.category ?? ''}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              category: (e.target.value || undefined) as ServiceOrderCategory | undefined,
              page: 1,
            }))
          }
          style={selectStyle}
        >
          <option value="">전체 분류</option>
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') applySearch();
          }}
          placeholder="오더 번호나 설명으로 검색"
          style={{ ...selectStyle, width: 220 }}
        />
        <Button type="button" variant="secondary" onClick={applySearch}>
          검색
        </Button>
        {/* batch-7 — 공개 채널 (PUB) 필터 toggle. SO-YYYYMMDD-PUB### 패턴만. */}
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            padding: '4px 10px',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-input)',
            background: pubOnly ? 'var(--bg-accent-subtle)' : 'var(--bg-surface)',
            color: pubOnly ? 'var(--fg-brand)' : 'var(--fg-default)',
            cursor: 'pointer',
            fontWeight: pubOnly ? 600 : 400,
          }}
          title="공개 채널로 들어온 AS 신청만 보여 줍니다"
        >
          <input
            type="checkbox"
            checked={pubOnly}
            onChange={(e) => setPubOnly(e.target.checked)}
            style={{ margin: 0 }}
          />
          공개 신청만
        </label>
        {/* batch-12 §6 — list row 에 마스킹된 reporter phone 표시 옵션. */}
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            padding: '4px 10px',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-input)',
            background: showReporter ? 'var(--bg-accent-subtle)' : 'var(--bg-surface)',
            color: showReporter ? 'var(--fg-brand)' : 'var(--fg-default)',
            cursor: 'pointer',
            fontWeight: showReporter ? 600 : 400,
          }}
          title="공개 신청 줄에 일부를 가린 휴대폰 번호를 보여 줍니다. 예: 010-****-5678"
        >
          <input
            type="checkbox"
            checked={showReporter}
            onChange={(e) => {
              const next = e.target.checked;
              setShowReporter(next);
              // batch-13 §5 — PII 표시 toggle 의 server-side audit log.
              // fire-and-forget — UX 우선 (실패해도 toggle 동작은 정상).
              void auditEventsApi.record({
                action: 'ui.inbox.reporter_phone_visibility.toggle',
                metadata: { newState: next ? 'on' : 'off' },
              });
            }}
            style={{ margin: 0 }}
          />
          <Icon name="phone" size={14} aria-hidden /> 신청자 표시
        </label>
        {/* round7 §1-5 (D4) — ADMIN 전용 '삭제됨 포함' 토글. */}
        {isAdmin && (
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              padding: '4px 10px',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--r-input)',
              background: includeDeleted ? 'var(--bg-accent-subtle)' : 'var(--bg-surface)',
              color: includeDeleted ? 'var(--fg-brand)' : 'var(--fg-default)',
              cursor: 'pointer',
              fontWeight: includeDeleted ? 600 : 400,
            }}
            title="삭제된 서비스 오더도 함께 보여 줍니다. 여기서 복구할 수 있습니다."
          >
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              style={{ margin: 0 }}
            />
            삭제됨 포함
          </label>
        )}
        <div
          className="kuun-caption"
          style={{
            marginLeft: 'auto',
            color: listQ.isFetching ? 'var(--brand)' : 'var(--fg-muted)',
          }}
        >
          {listQ.isFetching ? '불러오는 중…' : `${listQ.data?.total ?? 0} 건`}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, flex: 1, minHeight: 0 }}>
        {/* List (left) */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-card)',
            background: 'var(--bg-surface)',
            overflow: 'auto',
          }}
        >
          {listQ.isLoading ? (
            <div style={empty}>
              <span className="kuun-caption">불러오는 중…</span>
            </div>
          ) : listQ.error ? (
            <div style={empty}>
              <span style={{ color: 'var(--fg-danger)' }}>{extractApiError(listQ.error)}</span>
            </div>
          ) : items.length === 0 ? (
            <div style={empty} className="kuun-dot-grid">
              <div className="kuun-h4">조건에 맞는 서비스 오더가 없습니다</div>
              <div className="kuun-caption" style={{ marginTop: 4 }}>
                인수증에 서명하거나 고객이 리포트를 보내면 서비스 오더가 자동으로 만들어집니다.
              </div>
            </div>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  {/* §⑥ — 좌측 4px 컬러바 (P1 pulse). 헤더 라벨 없음. */}
                  <th style={{ ...th, width: 4, padding: 0 }} aria-hidden />
                  <th style={th}>등급</th>
                  <th style={th}>ID</th>
                  <th style={th}>상태</th>
                  <th style={th}>분류</th>
                  <th style={th}>요약</th>
                  <th style={th}>예약일</th>
                  <th style={th}>마감</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const breached = isAnyBreach(r);
                  const countdown = formatSlaCountdown(r);
                  return (
                    <tr
                      key={r.id}
                      onClick={() => setSelectedId(r.id)}
                      style={{
                        cursor: 'pointer',
                        borderTop: '1px solid var(--border-subtle)',
                        background: selectedId === r.id ? 'var(--bg-selected)' : undefined,
                      }}
                    >
                      <td style={{ padding: 0, width: 4 }}>
                        <StatusBar
                          kind="report"
                          status={statusToReportStage(r.status)}
                          urgent={r.severity === ServiceSeverity.P1}
                          width={4}
                          height={36}
                          aria-label={`severity-${r.severity}-status-${r.status}`}
                        />
                      </td>
                      <td style={td}>
                        <span style={severityChip(r.severity)} title={SEVERITY_TOOLTIP[r.severity]}>
                          {SEVERITY_LABEL[r.severity]}
                        </span>
                        {breached && (
                          <span title="SLA 마감 초과" style={breachChip}>
                            SLA 초과
                          </span>
                        )}
                        {countdown && !breached && (
                          <span
                            title="다음 SLA 마감까지 남은 시간"
                            style={countdownChip(countdown.urgent)}
                            data-testid={`sla-countdown-${r.id}`}
                          >
                            {countdown.text}
                          </span>
                        )}
                      </td>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {r.humanId}
                          {/* batch-10 — 시트 행에 origin badge (detail rail 의 chip 과 동일 의미). */}
                          {r.origin === 'PUBLIC' && (
                            <span
                              style={{
                                fontSize: 9,
                                padding: '0 4px',
                                borderRadius: 'var(--r-pill)',
                                background: 'var(--bg-info-subtle)',
                                color: 'var(--fg-brand)',
                                fontWeight: 700,
                              }}
                              title="공개 채널 신청"
                              aria-label="공개 채널 신청"
                            >
                              PUB
                            </span>
                          )}
                          {/* round7 §1-5 (D4) — 삭제됨 행 배지(ADMIN includeDeleted 목록). */}
                          {r.deletedAt && (
                            <span
                              style={{
                                fontSize: 9,
                                padding: '0 4px',
                                borderRadius: 'var(--r-pill)',
                                background: 'var(--bg-danger-subtle)',
                                color: 'var(--fg-danger)',
                                fontWeight: 700,
                              }}
                              title="삭제된 서비스 오더입니다. 복구할 수 있습니다."
                              aria-label="삭제됨"
                            >
                              삭제됨
                            </span>
                          )}
                          {/* batch-12 §6 — showReporter ON + 공개 신청 + phone 있음 → 마스킹된 phone. */}
                          {showReporter && r.origin === 'PUBLIC' && r.publicReporterPhone && (
                            <span
                              style={{
                                fontSize: 10,
                                color: 'var(--fg-muted)',
                                fontFamily: 'var(--font-mono)',
                                marginLeft: 2,
                              }}
                              title="공개 신청자 휴대폰"
                            >
                              {r.publicReporterPhone}
                            </span>
                          )}
                        </span>
                      </td>
                      <td style={td}>{STATUS_LABEL[r.status]}</td>
                      <td style={td}>{CATEGORY_LABEL[r.category]}</td>
                      <td
                        style={{
                          ...td,
                          maxWidth: 280,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {r.description}
                      </td>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                        {r.scheduledFor ? fmtKrTime(r.scheduledFor) : '–'}
                      </td>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                        {fmtNextDeadline(r)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail (right) */}
        <div
          style={{
            width: 420,
            flexShrink: 0,
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-card)',
            background: 'var(--bg-surface)',
            overflow: 'auto',
          }}
        >
          {!selectedId ? (
            <div style={empty}>
              <span className="kuun-caption">서비스 오더를 선택해 주세요.</span>
            </div>
          ) : selectedQ.isLoading ? (
            <div style={empty}>
              <span className="kuun-caption">불러오는 중…</span>
            </div>
          ) : selectedQ.error ? (
            <div style={empty}>
              <span style={{ color: 'var(--fg-danger)' }}>{extractApiError(selectedQ.error)}</span>
            </div>
          ) : (
            detail && (
              <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <div className="kuun-label">현재 상태</div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{STATUS_LABEL[detail.status]}</div>
                  <div
                    style={{
                      ...severityChip(detail.severity),
                      marginLeft: 'auto',
                    }}
                  >
                    <span title={SEVERITY_TOOLTIP[detail.severity]}>
                      {SEVERITY_LABEL[detail.severity]}
                    </span>
                  </div>
                </div>
                <KV
                  k="ID"
                  v={
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontFamily: 'var(--font-mono)' }}>{detail.humanId}</span>
                      {/* batch-9 — 공개 채널 origin badge. */}
                      {detail.origin === 'PUBLIC' && (
                        <span
                          style={{
                            fontSize: 10,
                            padding: '2px 6px',
                            borderRadius: 'var(--r-pill)',
                            background: 'var(--bg-info-subtle)',
                            color: 'var(--fg-brand)',
                            border: '1px solid var(--border-info-saturated)',
                            fontWeight: 600,
                          }}
                          title="고객이 공개 AS 신청 페이지에서 신청했습니다"
                        >
                          공개
                        </span>
                      )}
                    </span>
                  }
                />
                <KV k="분류" v={CATEGORY_LABEL[detail.category]} />
                {detail.scheduledFor && <KV k="예약일" v={fmtKrTime(detail.scheduledFor)} />}
                {detail.assignedToId && (
                  <KV
                    k="배정"
                    v={<span title={detail.assignedToId}>{resolveUser(detail.assignedToId)}</span>}
                  />
                )}
                {detail.cancelReason && <KV k="취소 사유" v={detail.cancelReason} />}
                {/* round5 #14 — 출처 리포트 상호 링크. 자동 승격(REACTIVE)
                    SO 는 접수 원본인 고객 리포트로 바로 이동할 수 있다. */}
                {detail.triggeredByReportId && (
                  <KV
                    k="출처"
                    v={
                      <button
                        type="button"
                        onClick={() => navigate('/service-orders?view=reports')}
                        data-testid="so-open-source-report"
                        style={{
                          padding: '1px 8px',
                          fontSize: 12,
                          border: '1px solid var(--border-default)',
                          background: 'var(--bg-surface)',
                          borderRadius: 'var(--r-pill)',
                          cursor: 'pointer',
                          color: 'var(--fg-brand)',
                        }}
                        title="고객 리포트 탭에서 접수 원본 보기"
                      >
                        접수한 고객 리포트 보기
                      </button>
                    }
                  />
                )}
                {/* batch-9 — 공개 채널 reporter 정보. PII 마스킹 (휴대폰 끝 4자리). */}
                {detail.origin === 'PUBLIC' && detail.publicReporterPhone && (
                  <KV
                    k="신청자 휴대폰"
                    v={
                      <span style={{ fontFamily: 'var(--font-mono)' }}>
                        {detail.publicReporterPhone}
                      </span>
                    }
                  />
                )}
                {detail.origin === 'PUBLIC' && detail.publicReporterStore && (
                  <KV k="신청자 매장" v={detail.publicReporterStore} />
                )}
                <div>
                  <div className="kuun-label" style={{ marginBottom: 4 }}>
                    설명
                  </div>
                  <div
                    style={{
                      padding: 10,
                      background: 'var(--bg-sunken)',
                      borderRadius: 'var(--r-card)',
                      fontSize: 13,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {detail.description}
                  </div>
                </div>

                {/* SLA panel */}
                <div>
                  <div className="kuun-label" style={{ marginBottom: 4 }}>
                    SLA 마감
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                    <SlaRow
                      label="분류"
                      deadline={detail.triageDeadlineAt}
                      clearedAt={detail.triagedAt}
                    />
                    <SlaRow
                      label="해결"
                      deadline={detail.resolveDeadlineAt}
                      clearedAt={detail.resolvedAt}
                    />
                    <SlaRow
                      label="검증"
                      deadline={detail.verifyDeadlineAt}
                      clearedAt={detail.verifiedAt}
                    />
                  </div>
                </div>

                {/* round7 §1-5 (D4) — 삭제됨 배너 + 복구(ADMIN). soft-delete 된
                    행은 ADMIN 만 상세로 열 수 있고, 여기서 복구한다. 일반
                    액션/취소/재배정 레일은 아래에서 !deletedAt 로 숨긴다. */}
                {detail.deletedAt && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      paddingTop: 8,
                      borderTop: '1px solid var(--border-subtle)',
                    }}
                    data-testid="so-deleted-rail"
                  >
                    <div className="kuun-label" style={{ color: 'var(--fg-danger)' }}>
                      삭제됨
                    </div>
                    <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
                      이 서비스 오더는 삭제되어 운영 목록에서 숨겨져 있습니다.
                    </div>
                    {isAdmin && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={restoreDeletedMx.isPending}
                          onClick={() => restoreDeletedMx.mutate(detail.id)}
                          data-testid="so-restore-deleted-button"
                        >
                          복구
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Action rail */}
                {canEdit && !detail.deletedAt && !isTerminalStatus(detail.status) && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      paddingTop: 8,
                      borderTop: '1px solid var(--border-subtle)',
                    }}
                  >
                    <div className="kuun-label">액션</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {nextStatuses.map((s) => (
                        <Button
                          key={s}
                          type="button"
                          variant="secondary"
                          disabled={transitionMx.isPending}
                          onClick={() => transitionMx.mutate({ id: detail.id, to: s })}
                        >
                          {withRo(STATUS_LABEL[s])}
                        </Button>
                      ))}
                      {/* round7 §1-5 (D5) — 등급 재분류. 공개 접수는 P2 고정으로
                          들어오므로 실제 긴급도에 맞춰 조정. */}
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={severityMx.isPending}
                        onClick={handleChangeSeverity}
                        data-testid="so-change-severity-button"
                      >
                        등급 변경
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={cancelMx.isPending}
                        onClick={handleCancel}
                      >
                        취소
                      </Button>
                    </div>
                  </div>
                )}

                {/* Admin-only restore rail — surfaces only on
                    terminal rows (CLOSED / CANCELLED). The wider
                    ADMIN gate is enforced server-side in
                    POST /admin/service-orders/:id/restore; the
                    UI gate keeps the button off the screen for
                    non-ADMIN / non-terminal cases so we don't
                    show a dangling clickable surface that would
                    just 4xx. */}
                {isAdmin && !detail.deletedAt && isTerminalStatus(detail.status) && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      paddingTop: 8,
                      borderTop: '1px solid var(--border-subtle)',
                    }}
                    data-testid="admin-restore-rail"
                  >
                    <div className="kuun-label">관리자</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={restoreMx.isPending}
                        onClick={handleRestore}
                        data-testid="admin-restore-button"
                      >
                        복구
                      </Button>
                    </div>
                  </div>
                )}

                {/* Admin-only reassign-customer rail — surfaces
                    on NON-terminal rows (the inverse of restore).
                    Server-side rejects reassign on terminal rows
                    with a 400 pointing back at /restore first;
                    we gate at the UI layer to keep the button off
                    those rows entirely. ADMIN check mirrors the
                    server's @Roles(ADMIN) on the controller. */}
                {isAdmin && !detail.deletedAt && !isTerminalStatus(detail.status) && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      paddingTop: 8,
                      borderTop: '1px solid var(--border-subtle)',
                    }}
                    data-testid="admin-reassign-customer-rail"
                  >
                    <div className="kuun-label">관리자</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={reassignCustomerMx.isPending}
                        onClick={handleReassignCustomer}
                        data-testid="admin-reassign-customer-button"
                      >
                        고객 재배정
                      </Button>
                    </div>
                  </div>
                )}

                {/* round7 §1-5 (D4) — soft-delete 레일. 매니저 이상이 잘못
                    생성/중복 SO 를 제거(상태 무관, 삭제된 행 제외). */}
                {canEdit && !detail.deletedAt && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      paddingTop: 8,
                      borderTop: '1px solid var(--border-subtle)',
                    }}
                    data-testid="so-delete-rail"
                  >
                    <div className="kuun-label">삭제</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={deleteMx.isPending}
                        onClick={handleDelete}
                        data-testid="so-delete-button"
                        style={{ color: 'var(--fg-danger)' }}
                      >
                        서비스 오더 삭제
                      </Button>
                    </div>
                  </div>
                )}

                {/* round7 §1-6 (D7) — 접수 사진 (공개 신청 첨부 + 운영자 첨부). */}
                {(attachmentsQ.data?.length ?? 0) > 0 && (
                  <div data-testid="so-attachments">
                    <div className="kuun-label" style={{ marginBottom: 6 }}>
                      접수 사진 ({attachmentsQ.data?.length ?? 0})
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
                        gap: 6,
                      }}
                    >
                      {attachmentsQ.data?.map((att) => (
                        <a
                          key={att.id}
                          href={att.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={att.originalFilename ?? '접수 사진'}
                          style={{
                            display: 'block',
                            aspectRatio: '1 / 1',
                            borderRadius: 'var(--r-card)',
                            overflow: 'hidden',
                            border: '1px solid var(--border-default)',
                          }}
                        >
                          <img
                            src={att.downloadUrl}
                            alt={att.originalFilename ?? '접수 사진'}
                            loading="lazy"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block',
                            }}
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timeline */}
                <div>
                  <div className="kuun-label" style={{ marginBottom: 4 }}>
                    타임라인
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                      maxHeight: 240,
                      overflow: 'auto',
                    }}
                  >
                    {detail.events.map((evt) => {
                      const { label, summary } = formatServiceOrderEvent(evt);
                      const text = noteBodyOf(evt) ?? summary;
                      return (
                        <div
                          key={evt.id}
                          style={{
                            fontSize: 12,
                            padding: 8,
                            background: 'var(--bg-sunken)',
                            borderRadius: 'var(--r-card)',
                          }}
                          data-testid={`timeline-event-${evt.id}`}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              color: 'var(--fg-muted)',
                            }}
                          >
                            <span>
                              <span style={{ fontWeight: 600 }}>{label}</span>
                              {/* 메모는 "누가 남겼는지"가 핵심이라 작성자를 함께 노출. */}
                              <span
                                style={{ marginLeft: 6 }}
                                title={evt.actorUserId ?? undefined}
                                data-testid={`timeline-event-${evt.id}-actor`}
                              >
                                {evt.actorUserId ? resolveUser(evt.actorUserId) : '시스템'}
                              </span>
                            </span>
                            <span style={{ fontFamily: 'var(--font-mono)' }}>
                              {fmtKrTime(evt.createdAt)}
                            </span>
                          </div>
                          {text && (
                            <div
                              style={{
                                marginTop: 4,
                                color: 'var(--fg-default)',
                                lineHeight: 1.4,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                              data-testid={`timeline-event-${evt.id}-summary`}
                            >
                              {text}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {detail.events.length === 0 && <span className="kuun-caption">기록 없음</span>}
                  </div>

                  {/* 계획 §8-2 — 메모 추가. 서버 권한(WRITE_ROLES)과 동일하게
                      매니저 이상에게만 노출하고, 종결된 SO 에서도 남길 수 있다. */}
                  {canEdit && (
                    <div style={{ marginTop: 8 }} data-testid="so-note-form">
                      <Textarea
                        value={noteBody}
                        onChange={(e) => setNoteBody(e.target.value)}
                        placeholder="메모를 적어 주세요"
                        maxLength={4096}
                        rows={2}
                        style={{ minHeight: 56, fontSize: 12 }}
                        data-testid="so-note-input"
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                        <Button
                          variant="secondary"
                          disabled={noteBody.trim().length === 0 || addNoteMx.isPending}
                          onClick={() => addNoteMx.mutate({ id: detail.id, body: noteBody.trim() })}
                          data-testid="so-note-submit"
                        >
                          추가
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      </div>

      {/* ──────────────────────────────────────────────────────
          Confirmation modals — replace the previous
          window.prompt sequences for cancel / restore /
          reassign-customer. Source context + form inputs +
          consistent footer; Plan §G-1 (backdrop click is NOT
          a dismiss to protect mid-typing input).
          ────────────────────────────────────────────────────── */}
      {detail && (
        <>
          <ConfirmActionModal
            open={cancelModalOpen}
            onClose={() => {
              if (cancelMx.isPending) return;
              setCancelModalOpen(false);
              setCancelReason('');
            }}
            title="서비스 오더 취소"
            submitLabel="취소 처리"
            submitVariant="danger"
            submitDisabled={cancelReason.trim().length === 0}
            isSubmitting={cancelMx.isPending}
            onSubmit={submitCancel}
            context={
              <div>
                <div>
                  <strong>{detail.humanId}</strong> · 현재 상태:{' '}
                  {STATUS_LABEL[detail.status as ServiceOrderStatus]}
                </div>
                <div>사유를 기록하고 취소 상태로 바꿉니다.</div>
              </div>
            }
          >
            <Field label="취소 사유" required>
              <Textarea
                data-testid="cancel-reason-input"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="예: 고객 요청으로 중단"
                rows={4}
              />
            </Field>
          </ConfirmActionModal>

          <ConfirmActionModal
            open={restoreModalOpen}
            onClose={() => {
              if (restoreMx.isPending) return;
              setRestoreModalOpen(false);
              setRestoreReason('');
            }}
            title="서비스 오더 복구"
            submitLabel="복구"
            submitDisabled={restoreReason.trim().length === 0}
            isSubmitting={restoreMx.isPending}
            onSubmit={submitRestore}
            context={
              <div>
                <div>
                  <strong>{detail.humanId}</strong> · 현재 상태:{' '}
                  {STATUS_LABEL[detail.status as ServiceOrderStatus]}
                </div>
                <div>복구하면 신규 상태로 돌아가고 종료 시각이 지워집니다.</div>
              </div>
            }
          >
            <Field label="복구 사유" required>
              <Textarea
                data-testid="restore-reason-input"
                value={restoreReason}
                onChange={(e) => setRestoreReason(e.target.value)}
                placeholder="예: 고객 요청으로 재오픈"
                rows={4}
              />
            </Field>
          </ConfirmActionModal>

          <ConfirmActionModal
            open={reassignModalOpen}
            onClose={() => {
              if (reassignCustomerMx.isPending) return;
              setReassignModalOpen(false);
              setReassignCustomer(null);
              setReassignStore(null);
              setReassignReason('');
            }}
            title="고객 재배정"
            submitLabel="재배정"
            submitDisabled={reassignCustomer === null || reassignReason.trim().length === 0}
            isSubmitting={reassignCustomerMx.isPending}
            onSubmit={submitReassignCustomer}
            context={
              <div>
                <div>
                  <strong>{detail.humanId}</strong> · 현재 고객 ID: <code>{detail.customerId}</code>
                </div>
                <div>현재 매장 ID: {detail.storeId ? <code>{detail.storeId}</code> : '없음'}</div>
              </div>
            }
          >
            <Field label="새 고객" required>
              <CustomerPicker
                value={reassignCustomer}
                onChange={(c) => {
                  setReassignCustomer(c);
                  // Changing the customer invalidates any
                  // previously-picked store from the old
                  // customer's branch list. Clear it so the
                  // server doesn't reject with "store does not
                  // belong to customer".
                  if (c?.id !== reassignCustomer?.id) {
                    setReassignStore(null);
                  }
                }}
                data-testid="reassign-customer-picker"
              />
            </Field>
            <Field
              label="새 매장 (선택)"
              hint={
                reassignCustomer
                  ? '비워 두면 지금 매장 연결이 풀립니다.'
                  : '고객을 먼저 선택해 주세요.'
              }
            >
              <StorePicker
                value={reassignStore}
                onChange={setReassignStore}
                customerId={reassignCustomer?.id ?? null}
                disabled={reassignCustomer === null}
                data-testid="reassign-store-picker"
              />
            </Field>
            <Field label="재배정 사유" required>
              <Textarea
                data-testid="reassign-reason-input"
                value={reassignReason}
                onChange={(e) => setReassignReason(e.target.value)}
                placeholder="예: 고객사 합병으로 인한 재배정"
                rows={3}
              />
            </Field>
          </ConfirmActionModal>

          {/* round7 §1-5 (D5) — 등급 재분류 모달. SLA 마감이 재계산됨을 안내. */}
          <ConfirmActionModal
            open={severityModalOpen}
            onClose={() => {
              if (severityMx.isPending) return;
              setSeverityModalOpen(false);
              setSeverityReason('');
            }}
            title="등급 변경"
            submitLabel="등급 변경"
            submitDisabled={severityReason.trim().length === 0 || severityValue === detail.severity}
            isSubmitting={severityMx.isPending}
            onSubmit={submitSeverity}
            context={
              <div>
                <div>
                  <strong>{detail.humanId}</strong> · 현재 등급: {SEVERITY_LABEL[detail.severity]}
                </div>
                <div>
                  등급을 바꾸면 분류, 해결, 검증 SLA 마감이 접수 시각을 기준으로 다시 계산됩니다.
                </div>
              </div>
            }
          >
            <Field label="새 등급" required>
              <select
                data-testid="severity-select"
                value={severityValue}
                onChange={(e) => setSeverityValue(e.target.value as ServiceSeverity)}
                style={selectStyle}
              >
                {Object.entries(SEVERITY_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
              <div className="kuun-caption" style={{ marginTop: 4, color: 'var(--fg-muted)' }}>
                {SEVERITY_TOOLTIP[severityValue]}
              </div>
            </Field>
            <Field label="변경 사유" required>
              <Textarea
                data-testid="severity-reason-input"
                value={severityReason}
                onChange={(e) => setSeverityReason(e.target.value)}
                placeholder="예: 현장 확인 결과 설비가 멈춰 긴급으로 올림"
                rows={3}
              />
            </Field>
          </ConfirmActionModal>
        </>
      )}
    </div>
  );
}

// ─── helpers ─────────────────────────────────────────────────────

function isAnyBreach(row: {
  triageDeadlineAt: string | null;
  resolveDeadlineAt: string | null;
  verifyDeadlineAt: string | null;
  triagedAt: string | null;
  resolvedAt: string | null;
  verifiedAt: string | null;
}): boolean {
  const now = Date.now();
  const triageBreached =
    row.triageDeadlineAt && !row.triagedAt && new Date(row.triageDeadlineAt).getTime() < now;
  const resolveBreached =
    row.resolveDeadlineAt && !row.resolvedAt && new Date(row.resolveDeadlineAt).getTime() < now;
  const verifyBreached =
    row.verifyDeadlineAt && !row.verifiedAt && new Date(row.verifyDeadlineAt).getTime() < now;
  return Boolean(triageBreached || resolveBreached || verifyBreached);
}

function fmtNextDeadline(row: {
  triageDeadlineAt: string | null;
  resolveDeadlineAt: string | null;
  verifyDeadlineAt: string | null;
  triagedAt: string | null;
  resolvedAt: string | null;
  verifiedAt: string | null;
}): string {
  // Show the earliest deadline that is still pending (not cleared by
  // its matching milestone).
  const pending: Array<{ at: string; label: string }> = [];
  if (row.triageDeadlineAt && !row.triagedAt)
    pending.push({ at: row.triageDeadlineAt, label: '분류' });
  if (row.resolveDeadlineAt && !row.resolvedAt)
    pending.push({ at: row.resolveDeadlineAt, label: '해결' });
  if (row.verifyDeadlineAt && !row.verifiedAt)
    pending.push({ at: row.verifyDeadlineAt, label: '검증' });
  if (pending.length === 0) return '–';
  pending.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  const first = pending[0];
  return `${first.label} ${fmtKrTime(first.at)}`;
}

// §⑥ Phase 2 — SLA countdown 텍스트 ("18분 남음" / "1h 남음").
// 가장 임박한 pending deadline 기준. 1시간 미만이면 urgent=true.
function formatSlaCountdown(row: {
  triageDeadlineAt: string | null;
  resolveDeadlineAt: string | null;
  verifyDeadlineAt: string | null;
  triagedAt: string | null;
  resolvedAt: string | null;
  verifiedAt: string | null;
}): { text: string; urgent: boolean } | null {
  const now = Date.now();
  const pending: number[] = [];
  if (row.triageDeadlineAt && !row.triagedAt)
    pending.push(new Date(row.triageDeadlineAt).getTime());
  if (row.resolveDeadlineAt && !row.resolvedAt)
    pending.push(new Date(row.resolveDeadlineAt).getTime());
  if (row.verifyDeadlineAt && !row.verifiedAt)
    pending.push(new Date(row.verifyDeadlineAt).getTime());
  if (pending.length === 0) return null;
  const next = Math.min(...pending);
  const diffMs = next - now;
  if (diffMs <= 0) return null; // breached — 별도 칩 (SLA↑) 가 처리
  const mins = Math.floor(diffMs / 60_000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  let text: string;
  if (days >= 1) text = `${days}일 남음`;
  else if (hours >= 1) text = `${hours}h ${mins % 60}m`;
  else text = `${mins}분 남음`;
  return { text, urgent: mins < 60 };
}

function countdownChip(urgent: boolean): CSSProperties {
  return {
    marginLeft: 6,
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 'var(--r-pill)',
    fontSize: 10,
    fontWeight: 600,
    fontFamily: 'var(--font-mono)',
    color: urgent ? 'var(--severity-urgent)' : 'var(--fg-muted)',
    border: `1px solid ${urgent ? 'var(--severity-urgent)' : 'var(--border-default)'}`,
    background: urgent ? 'var(--bg-danger-subtle)' : 'var(--bg-surface)',
  };
}

// §⑥ Phase 2 — Severity summary card. P1 는 좌측 펄스 컬러바.
// 클릭 시 severity filter 토글. 트리아지 큐 hero 의 visual anchor.
function SeverityCard({
  severity,
  count,
  active,
  onClick,
}: {
  severity: ServiceSeverity;
  count: number;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  const isP1 = severity === ServiceSeverity.P1;
  const color = isP1
    ? 'var(--severity-urgent)'
    : severity === ServiceSeverity.P2
      ? 'var(--severity-warning)'
      : 'var(--fg-muted)';
  const label =
    severity === ServiceSeverity.P1 ? '긴급' : severity === ServiceSeverity.P2 ? '주의' : '일반';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={`severity-card-${severity}`}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        gap: 12,
        padding: 0,
        background: active ? 'var(--bg-selected)' : 'var(--bg-surface)',
        border: `1px solid ${active ? color : 'var(--border-default)'}`,
        borderRadius: 'var(--r-card)',
        cursor: 'pointer',
        overflow: 'hidden',
        textAlign: 'left',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <div
        aria-hidden
        className={isP1 ? 'kuun-urgent' : undefined}
        style={{ width: 4, background: color, flexShrink: 0 }}
      />
      <div
        style={{
          flex: 1,
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span
            style={{
              fontSize: 11,
              color: 'var(--fg-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontWeight: 500,
            }}
          >
            {severity} · {label}
          </span>
          <span
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: count > 0 ? color : 'var(--fg-muted)',
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.02em',
            }}
          >
            {count}
          </span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--fg-subtle)' }}>건</span>
      </div>
    </button>
  );
}

function severityChip(s: ServiceSeverity): CSSProperties {
  const color =
    s === ServiceSeverity.P1
      ? 'var(--fg-danger)'
      : s === ServiceSeverity.P2
        ? 'var(--fg-warning)'
        : 'var(--fg-muted)';
  return {
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: 'var(--r-pill)',
    fontSize: 11,
    fontWeight: 600,
    color,
    border: `1px solid ${color}`,
  };
}

const breachChip: CSSProperties = {
  marginLeft: 6,
  display: 'inline-block',
  padding: '1px 6px',
  borderRadius: 'var(--r-pill)',
  fontSize: 10,
  fontWeight: 700,
  background: 'var(--bg-danger-subtle)',
  color: 'var(--fg-danger)',
  border: '1px solid var(--fg-danger)',
};

function SlaRow({
  label,
  deadline,
  clearedAt,
}: {
  label: string;
  deadline: string | null;
  clearedAt: string | null;
}) {
  if (!deadline) return null;
  const now = Date.now();
  const dl = new Date(deadline).getTime();
  const breached = !clearedAt && dl < now;
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <span style={{ width: 40, color: 'var(--fg-muted)' }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-mono)' }}>{fmtKrTime(deadline)}</span>
      {clearedAt ? (
        <Icon name="check" size={14} color="var(--fg-success)" aria-hidden />
      ) : breached ? (
        <span style={{ color: 'var(--fg-danger)', fontWeight: 600 }}>초과</span>
      ) : (
        <span style={{ color: 'var(--fg-muted)' }}>대기</span>
      )}
    </div>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 13 }}>
      <div style={{ width: 72, color: 'var(--fg-muted)' }}>{k}</div>
      <div style={{ minWidth: 0, wordBreak: 'break-all' }}>{v}</div>
    </div>
  );
}

const tableStyle: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};
const td: CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
const empty: CSSProperties = {
  height: '100%',
  minHeight: 320,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--fg-muted)',
  padding: 32,
};
const selectStyle: CSSProperties = {
  height: 32,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  fontSize: 13,
};
